import { useAtom } from 'jotai';
import { useCallback } from 'react';
import {
  addTransferJobAtom,
  clearTransferJobsAtom,
  deleteTransferJobAtom,
  getTransferJobAtom,
  transferJobsAtom,
  updateTransferJobAtom,
} from '@/atoms/transfer-jobs';
import { TransferJob } from '@/types/transfer-job';

export function useTransferJobs() {
  const [jobs] = useAtom(transferJobsAtom);
  const [, addJob] = useAtom(addTransferJobAtom);
  const [, updateJob] = useAtom(updateTransferJobAtom);
  const [, clearJobs] = useAtom(clearTransferJobsAtom);
  const [, deleteJob] = useAtom(deleteTransferJobAtom);
  const [getJob] = useAtom(getTransferJobAtom);

  const createJob = useCallback(
    (
      jobData: Omit<TransferJob, 'createdAt' | 'status'>,
      status: TransferJob['status'] = 'pending',
    ) => {
      const job: Omit<TransferJob, 'createdAt'> = {
        ...jobData,
        status,
      };
      addJob(job);
    },
    [addJob],
  );

  const markJobAsActive = useCallback(
    (jobId: string) => {
      updateJob({ jobId, updates: { status: 'active' } });
    },
    [updateJob],
  );

  const markJobAsCompleted = useCallback(
    (jobId: string) => {
      updateJob({ jobId, updates: { status: 'completed' } });
    },
    [updateJob],
  );

  const markJobAsFailed = useCallback(
    (jobId: string, error?: string) => {
      updateJob({ jobId, updates: { status: 'failed', error } });
    },
    [updateJob],
  );

  const getJobById = useCallback(
    (jobId: string) => {
      return getJob(jobId);
    },
    [getJob],
  );

  const hasActiveJobs = useMemo(
    () =>
      jobs.some((job) => job.status === 'active' || job.status === 'pending'),
    [jobs],
  );

  return {
    jobs,
    createJob,
    markJobAsActive,
    markJobAsCompleted,
    markJobAsFailed,
    getJobById,
    clearJobs,
    deleteJob,
    hasActiveJobs,
  };
}
